import React, { useMemo, useEffect, useState } from 'react';
import { Tag } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import hljs from 'highlight.js';
import 'highlight.js/styles/atom-one-dark.css';

const difficultyMap = {
  EZ: 'Easy',
  MD: 'Medium',
  HD: 'Hard',
  UN: 'Unknown',
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
};

const DIFFICULTY_COLORS = {
  easy: 'green',
  medium: 'orange',
  hard: 'red',
  unknown: 'default',
};

const getDifficultyColor = difficulty => {
  if (!difficulty) return 'default';
  const difficultyText = difficultyMap[difficulty] || difficulty;
  const normalizedDifficulty = difficultyText.toString().toLowerCase();
  return DIFFICULTY_COLORS[normalizedDifficulty] || 'default';
};

const safeExtractHtml = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.ops) {
    return '';
  }
  return String(value);
};

const MCQQuestion = ({ curr_question, selectedOptions = [] }) => {
  const { t } = useTranslation('practice_tests');
  const [forceRender, setForceRender] = useState(0);

  const selectedOptionIds = useMemo(() => {
    if (!selectedOptions || selectedOptions.length === 0) return new Set();
    return new Set(selectedOptions.map(opt => opt.option_id));
  }, [selectedOptions]);

  const quillModules = useMemo(
    () => ({
      syntax: {
        highlight: text => hljs.highlightAuto(text).value,
      },
      toolbar: false,
    }),
    [],
  );

  useEffect(() => {
    if (curr_question) {
      setForceRender(prev => prev + 1);

      const timer = setTimeout(() => {
        const quillElements = document.querySelectorAll(
          '.ql-editor pre:not(.hljs), .ql-editor code:not(.hljs)',
        );
        quillElements.forEach(el => {
          try {
            hljs.highlightElement(el);
          } catch (error) {
            console.warn('Syntax highlighting failed for element:', error.message);
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [curr_question?.id]);

  const renderQuestionContent = question => {
    const questionText = question.question_text || question.text || 'No question text available';
    const questionFormat = question.format || 'text';

    switch (questionFormat) {
      case 'html':
        return (
          <ReactQuill
            key={`mcq-quill-${forceRender}`}
            value={safeExtractHtml(questionText)}
            readOnly={true}
            modules={quillModules}
            theme="bubble"
            style={{
              border: 'none',
              padding: 0,
              fontSize: '15px',
              lineHeight: '1.4',
              fontWeight: '400',
              color: '#262626',
            }}
          />
        );
      case 'markdown':
        return <ReactMarkdown rehypePlugins={[rehypeHighlight]}>{questionText}</ReactMarkdown>;
      case 'text':
      default:
        return <ReactMarkdown rehypePlugins={[rehypeHighlight]}>{questionText}</ReactMarkdown>;
    }
  };

  return (
    <div
      style={{
        padding: '24px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        marginBottom: '20px',
      }}
    >
      {/* Question Points, Difficulty, and Tags */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        data-test-id="question-metadata"
        data-specific-id="question-header-info"
      >
        <div
          style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}
          data-test-id="question-info"
          data-specific-id="question-info-tags"
        >
          <Tag
            style={{ border: 'none', background: 'white' }}
            data-test-id="points-tag"
            data-specific-id="question-points"
          >
            {t('practice_tests.test_result.points_label', { points: curr_question.points || '-' })}
          </Tag>
          <Tag
            color={getDifficultyColor(curr_question.difficulty)}
            style={{ padding: '4px 8px' }}
            data-test-id="difficulty-tag"
            data-specific-id="question-difficulty"
          >
            {difficultyMap[curr_question.difficulty] || curr_question.difficulty || 'Medium'}
          </Tag>
          {curr_question.tags &&
            curr_question.tags.length > 0 &&
            curr_question.tags.map((tag, index) => (
              <Tag
                key={`${curr_question.id}-tag-${tag.id || index}`}
                color="default"
                style={{ padding: '4px 8px' }}
                data-test-id={`question-tag-${index}`}
                data-specific-id={`question-tag-${tag.id || tag.name || tag}`}
              >
                {tag.name || tag}
              </Tag>
            ))}
        </div>
      </div>

      {/* Topic and Subtopic Tags */}
      <div
        style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '12px 0px' }}
        data-test-id="topic-subtopic-tags"
        data-specific-id="question-categories"
      >
        {curr_question.topic && (
          <Tag
            style={{
              borderRadius: '4px',
              padding: '4px 8px',
              color: 'rgba(24, 144, 255, 1)',
              border: '1px solid rgba(145, 213, 255, 1)',
              background: '#E6F7FF',
            }}
            data-test-id="topic-tag"
            data-specific-id="question-topic"
          >
            {t('practice_tests.test_result.topic', { topic: curr_question.topic })}
          </Tag>
        )}
        {curr_question.subtopic && (
          <Tag
            style={{
              borderRadius: '4px',
              padding: '4px 8px',
              color: 'rgba(24, 144, 255, 1)',
              border: '1px solid rgba(145, 213, 255, 1)',
              background: 'white',
            }}
            data-test-id="subtopic-tag"
            data-specific-id="question-subtopic"
          >
            {t('practice_tests.test_result.subtopic', { subtopic: curr_question.subtopic })}
          </Tag>
        )}
      </div>

      {/* Question Text */}
      <div
        style={{
          fontSize: '1.2em',
          color: '#333',
          marginBottom: '25px',
          lineHeight: '1.5',
          textAlign: 'left',
        }}
        data-test-id="question-text"
        data-specific-id="question-content"
      >
        {renderQuestionContent(curr_question)}
      </div>

      {/* MCQ Options */}
      <div
        style={{ marginBottom: '25px' }}
        data-test-id="mcq-options"
        data-specific-id="question-options-section"
      >
        {selectedOptionIds.size === 0 && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffa940',
              borderRadius: '6px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Tag
              color="warning"
              style={{ margin: 0 }}
            >
              {t('practice_tests.test_result.not_submitted')}
            </Tag>
            <span style={{ color: '#d46b08', fontSize: '14px' }}>
              {t('practice_tests.test_result.no_answer_submitted')}
            </span>
          </div>
        )}

        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}
          data-test-id="options-list"
          data-specific-id="options-container"
        >
          {curr_question.options && curr_question.options.length > 0 ? (
            curr_question.options.map((option, index) => {
              const isCorrect = option.is_correct || option.isCorrect || option.correct;
              const isSelected = selectedOptionIds.has(option.id);
              const optionText =
                option.text || option.option_text || option.optionText || `Option ${index + 1}`;

              let borderColor = '#d9d9d9';
              let backgroundColor = 'white';
              let iconElement = null;

              if (isCorrect && isSelected) {
                borderColor = '#52c41a';
                backgroundColor = '#f6ffed';
                iconElement = (
                  <CheckCircleFilled style={{ color: '#52c41a', marginRight: '8px' }} />
                );
              } else if (isCorrect && !isSelected) {
                borderColor = '#95de64';
                backgroundColor = '#f6ffed';
                iconElement = (
                  <CheckCircleFilled style={{ color: '#95de64', marginRight: '8px' }} />
                );
              } else if (!isCorrect && isSelected) {
                borderColor = '#ff4d4f';
                backgroundColor = '#fff2f0';
                iconElement = (
                  <CloseCircleFilled style={{ color: '#ff4d4f', marginRight: '8px' }} />
                );
              }

              return (
                <div
                  key={`${curr_question.id}-option-${index}`}
                  style={{
                    border: `2px solid ${borderColor}`,
                    borderRadius: '6px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor,
                    textAlign: 'left',
                  }}
                  data-test-id={`option-${index}`}
                  data-specific-id={`mcq-option-${option.id || index}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px' }}>
                    {iconElement}
                    <span
                      style={{
                        fontSize: '1em',
                        color: '#555',
                        fontWeight: isCorrect || isSelected ? '500' : '400',
                        flex: 1,
                        textAlign: 'left',
                      }}
                      data-test-id={`option-text-${index}`}
                      data-specific-id={`option-label-${option.id || index}`}
                    >
                      {optionText}
                    </span>
                    {isCorrect && !isSelected && (
                      <Tag color="success">{t('practice_tests.test_result.correct_answer')}</Tag>
                    )}
                    {!isCorrect && isSelected && (
                      <Tag color="error">{t('practice_tests.test_result.your_answer')}</Tag>
                    )}
                    {isCorrect && isSelected && (
                      <Tag color="success">
                        {t('practice_tests.test_result.correct_your_answer')}
                      </Tag>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: '#999',
                backgroundColor: '#f5f5f5',
                borderRadius: '6px',
              }}
              data-test-id="no-options"
              data-specific-id="no-options-message"
            >
              {t('practice_tests.test_result.no_options')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MCQQuestion;
